import React, { useState, useEffect, useRef, lazy, Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import LoginPage from './pages/LoginPage';
import EnrollMfa from './components/EnrollMfa';
import styled from 'styled-components';
import Layout from './components/Layout';
import { ClaimWorklistProvider } from './components/ClaimWorklistContext';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class PageErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[PageErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', padding: '2rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#991b1b', marginBottom: '0.5rem' }}>
            Something went wrong loading this page.
          </div>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </div>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/worklist'; }}
            style={{
              padding: '0.5rem 1.25rem', background: '#334155', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            Back to Worklist
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load heavy components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClaimWorklist = lazy(() => import('./pages/ClaimWorklist'));
const MedCoderAI = lazy(() => import('./pages/MedCoderAI'));
const DemoEncounterLookup = lazy(() => import('./pages/DemoEncounterLookup'));
const ActiveWorklist = lazy(() => import('./pages/ActiveWorklist'));
const AutoClaimsPage = lazy(() => import('./pages/AutoClaimsPage'));

const queryClient = new QueryClient();

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const ProtectedRoute = ({ user }: { user: User | null }) => {
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
};

// Admin-only pages: everyone else is sent to the landing page.
const AdminRoute = ({ children }: { children: ReactNode }) => {
  const { profile } = useOutletContext<{ profile: { role: string } | null }>();
  if (!profile) return <SpinnerContainer>Loading...</SpinnerContainer>;
  if (profile.role?.toLowerCase() !== "admin") return <Navigate to="/auto-claims" replace />;
  return <>{children}</>;
};

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes idle timeout

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const WARNING_BEFORE_MS = 60 * 1000; // warn 1 minute before timeout

  useEffect(() => {
    const handleIdle = () => {
      setShowIdleWarning(false);
      signOut(auth);
    };
    const handleWarning = () => {
      setShowIdleWarning(true);
      idleTimerRef.current = setTimeout(handleIdle, WARNING_BEFORE_MS);
    };
    const resetTimer = () => {
      setShowIdleWarning(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      warningTimerRef.current = setTimeout(handleWarning, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        // Clear saved dept selection so the mandatory modal re-appears on next login
        sessionStorage.removeItem("medcode:selectedDepts");
        // Cached Encounter Search rows carry patient names.
        sessionStorage.removeItem("medcode:encounterSearchResults");
      }
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <SpinnerContainer>Loading...</SpinnerContainer>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      {showIdleWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 32,
            maxWidth: 400, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 12px', color: '#1a1a1a' }}>Session Expiring Soon</h3>
            <p style={{ margin: '0 0 24px', color: '#555' }}>
              You will be logged out in 1 minute due to inactivity.
            </p>
            <button
              onClick={() => setShowIdleWarning(false)}
              style={{
                background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 6, padding: '10px 24px', cursor: 'pointer',
                fontSize: 14, fontWeight: 600
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
      <ClaimWorklistProvider>
        <Router>
          <PageErrorBoundary>
          <Suspense fallback={<SpinnerContainer>Loading...</SpinnerContainer>}>
            <Routes>

              {/* Default redirect */}
              <Route path="/" element={<Navigate to={user ? "/auto-claims" : "/login"} replace />} />

              {/* Login Screen */}
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/enroll-mfa"
                element={
                  process.env.REACT_APP_REQUIRE_MFA === "false"
                    ? <Navigate to="/auto-claims" replace />
                    : <EnrollMfa />
                }
              />

              {/* Protected Wrapper */}
              <Route element={<ProtectedRoute user={user} />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
                  <Route path="/claim-worklist/:patientId/:appointmentDate" element={<ClaimWorklist />} />
                  <Route path="/medcoder-ai" element={<MedCoderAI />} />
                  {/* Production coding worklist — departments + active encounters */}
                  <Route path="/worklist" element={<ActiveWorklist />} />
                  {/* Encounter search by patient ID / claim ID — same table as the worklist */}
                  <Route path="/encounter-search" element={<ActiveWorklist key="encounter-search" searchOnly />} />
                  {/* Demo: load encounter directly from Athena without Firestore persistence */}
                  <Route path="/demo" element={<DemoEncounterLookup />} />
                  {/* Auto claim processing pipeline */}
                  <Route path="/auto-claims" element={<AutoClaimsPage />} />
                </Route>
              </Route>

            </Routes>
          </Suspense>
          </PageErrorBoundary>
        </Router>
      </ClaimWorklistProvider>
    </QueryClientProvider>
  );
}

export default App;
